import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";
import { Heart, Stethoscope, Home as HomeIcon, DollarSign } from "lucide-react";

function useCountUp(target, durationMs = 1400, start = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start || !target || target <= 0) {
      setValue(target || 0);
      return;
    }
    let raf;
    const t0 = performance.now();
    const from = 0;
    const step = (now) => {
      const t = Math.min(1, (now - t0) / durationMs);
      // ease-out
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(from + (target - from) * eased));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs, start]);
  return value;
}

function StatTile({ Icon, value, label, sublabel, start, prefix = "" }) {
  const displayed = useCountUp(value, 1400, start);
  return (
    <div className="text-center" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4"
        style={{ backgroundColor: "#E27E4E", color: "#FFFFFF" }}>
        <Icon size={22} />
      </div>
      <div className="font-serif text-5xl md:text-6xl leading-none mb-2" style={{ color: "#0E5C6E" }}>
        {prefix}{displayed.toLocaleString()}
      </div>
      <div className="text-xs uppercase tracking-[0.25em]" style={{ color: "#E27E4E" }}>{label}</div>
      {sublabel && <div className="text-xs mt-1" style={{ color: "#6B655E" }}>{sublabel}</div>}
    </div>
  );
}

export default function ImpactCounter() {
  const [impact, setImpact] = useState({ dogs_rescued: 0, surgeries_funded: 0, forever_families: 0, donations_raised: 0 });
  const [visible, setVisible] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    api.get("/stats")
      .then((r) => setImpact(r.data?.impact || {}))
      .catch((err) => logError("[ImpactCounter] failed to load stats:", err));
  }, []);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.25 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const dogs = impact.dogs_rescued || 0;
  const surg = impact.surgeries_funded || 0;
  const fam = impact.forever_families || 0;
  const donations = impact.donations_raised || 0;

  // Hide section entirely if all values are 0 (avoids showing embarrassing zeros before admin sets them)
  if (dogs === 0 && surg === 0 && fam === 0 && donations === 0) return null;

  return (
    <section ref={rootRef} className="py-20" style={{ backgroundColor: "#FDF9F1" }} data-testid="impact-counter-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <div className="text-xs uppercase tracking-[0.35em] mb-3" style={{ color: "#E27E4E" }}>Kuleana • Our Impact</div>
          <h2 className="font-serif text-4xl md:text-5xl" style={{ color: "#0E5C6E" }}>
            Every number is a life saved.
          </h2>
          <p className="mt-4 text-sm max-w-2xl mx-auto" style={{ color: "#6B655E" }}>
            Since 2024, our ʻohana of fosters, vets, and donors has turned aloha into action.
          </p>
        </div>

        <div className={`grid grid-cols-2 ${donations > 0 ? "md:grid-cols-4" : "md:grid-cols-3"} gap-10`}>
          {dogs > 0 && <StatTile Icon={Heart} value={dogs} label="Dogs Rescued" sublabel="from Hawaiʻi to the West Coast" start={visible} />}
          {surg > 0 && <StatTile Icon={Stethoscope} value={surg} label="Surgeries Funded" sublabel="eye, spay/neuter, life-saving care" start={visible} />}
          {fam > 0 && <StatTile Icon={HomeIcon} value={fam} label="Forever Families" sublabel="matched with their ʻohana" start={visible} />}
          {donations > 0 && <StatTile Icon={DollarSign} value={donations} label="Raised for Care" sublabel="100% to the pups" start={visible} prefix="$" />}
        </div>
      </div>
    </section>
  );
}
